import { forwardRef } from 'react'
import type { ReactNode, SelectHTMLAttributes } from 'react'

import { cn } from '../../lib/cn'
import { useFieldState } from './Field'
import { CONTROL_SIZES, controlSurface } from './Input'
import type { ControlSize, ControlTone } from './Input'
import { Icon } from './Icon'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  selectSize?: ControlSize
  tone?: ControlTone
  invalid?: boolean
  /** Convenience for flat lists; use `children` for grouped options. */
  options?: readonly SelectOption[]
  /** Rendered as a disabled first option — pick one, not "select…". */
  placeholder?: string
  children?: ReactNode
}

/**
 * A native select. Native is the right call here: it is keyboard-complete on every platform
 * and opens as a proper picker on touch, which no custom listbox matches.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    selectSize = 'md',
    tone = 'paper',
    invalid,
    options,
    placeholder,
    children,
    className,
    id,
    required,
    'aria-describedby': describedBy,
    ...rest
  },
  ref,
) {
  const field = useFieldState()
  const isInvalid = invalid ?? field?.invalid ?? false

  return (
    <div className="relative flex items-center">
      <select
        ref={ref}
        id={id ?? field?.id}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy ?? field?.describedBy}
        required={required ?? field?.required}
        className={cn(
          'ring-focus cursor-pointer appearance-none pr-9 pl-3',
          controlSurface(tone, isInvalid),
          CONTROL_SIZES[selectSize],
          className,
        )}
        {...rest}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
        {children}
      </select>
      <Icon
        name="chevron-down"
        size={16}
        className={cn(
          'pointer-events-none absolute right-3',
          tone === 'chrome' ? 'text-chrome-mute' : 'text-ink-faint',
        )}
      />
    </div>
  )
})
